import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { mockCategories } from '../mock/data';
import { Sparkles, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AIProjectCreation = ({ open, onOpenChange }) => {
  const [step, setStep] = useState(1); // 1: Input, 2: AI Generated, 3: Edit
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    scopeOfWork: '',
    deliverables: '',
    category: '',
    startDate: '',
    endDate: '',
    numberOfTasks: '',
    color: '#10b981'
  });
  const [generatedPlan, setGeneratedPlan] = useState(null);

  const handleGeneratePlan = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/ai/generate-project-plan`, {
        projectName: formData.name,
        description: formData.description,
        scopeOfWork: formData.scopeOfWork,
        deliverables: formData.deliverables,
        startDate: formData.startDate,
        endDate: formData.endDate,
        numberOfTasks: formData.numberOfTasks ? parseInt(formData.numberOfTasks) : null
      });
      
      setGeneratedPlan(response.data);
      setStep(2);
    } catch (error) {
      console.error('Error generating plan:', error);
      alert('Failed to generate project plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async () => {
    // This will be implemented with backend
    alert('Project creation with AI-generated plan will be implemented with backend');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            <span>AI-Assisted Project Creation</span>
          </DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4 mt-4">
            <div>
              <Label>Project Name *</Label>
              <Input
                placeholder="Enter project name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-1"
              />
            </div>

            <div>
              <Label>Description (Optional)</Label>
              <Textarea
                placeholder="Brief project description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="mt-1"
                rows={2}
              />
            </div>

            <div>
              <Label>Scope of Work *</Label>
              <Textarea
                placeholder="Detailed scope of work and objectives"
                value={formData.scopeOfWork}
                onChange={(e) => setFormData({ ...formData, scopeOfWork: e.target.value })}
                className="mt-1"
                rows={3}
              />
            </div>

            <div>
              <Label>Deliverables *</Label>
              <Textarea
                placeholder="Expected deliverables and outcomes"
                value={formData.deliverables}
                onChange={(e) => setFormData({ ...formData, deliverables: e.target.value })}
                className="mt-1"
                rows={3}
              />
            </div>

            <div>
              <Label>Tags (Optional)</Label>
              <Input
                placeholder="e.g., web, mobile, design (comma separated)"
                value={formData.tags || ''}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Help AI create better task names using tags</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>End Date *</Label>
                <Input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label>Number of Tasks (Optional)</Label>
              <Input
                type="number"
                placeholder="Let AI decide if left empty"
                value={formData.numberOfTasks}
                onChange={(e) => setFormData({ ...formData, numberOfTasks: e.target.value })}
                className="mt-1"
              />
            </div>

            <div>
              <Label>Category</Label>
              <Select value={formData.category} onValueChange={(val) => setFormData({ ...formData, category: val })}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {mockCategories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Color</Label>
              <div className="flex space-x-2 mt-2">
                {['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'].map(color => (
                  <button
                    key={color}
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      formData.color === color ? 'border-gray-900 scale-110' : 'border-gray-300'
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => setFormData({ ...formData, color })}
                  />
                ))}
              </div>
            </div>

            <Button
              className="w-full bg-purple-600 hover:bg-purple-700"
              onClick={handleGeneratePlan}
              disabled={loading || !formData.name || !formData.scopeOfWork || !formData.deliverables || !formData.startDate || !formData.endDate}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating Project Plan...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate Project Plan with AI
                </>
              )}
            </Button>
          </div>
        )}

        {step === 2 && generatedPlan && (
          <div className="space-y-4 mt-4">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h3 className="font-semibold text-purple-900 mb-2">AI-Generated Project Plan</h3>
              <p className="text-sm text-purple-700">Review and edit the plan below before creating the project</p>
            </div>

            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {generatedPlan.taskLists && generatedPlan.taskLists.map((taskList, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">{taskList.name}</h4>
                  <div className="space-y-2">
                    {taskList.tasks.map((task, taskIdx) => (
                      <div key={taskIdx} className="flex items-start space-x-3 p-2 bg-gray-50 rounded">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{task.title}</p>
                          <p className="text-xs text-gray-600 mt-1">{task.description}</p>
                          <div className="flex items-center space-x-3 mt-2">
                            <span className="text-xs text-gray-500">Weight: {task.weight}%</span>
                            <span className="text-xs text-gray-500">Est: {task.estimatedHours}h</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex space-x-3">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                Back to Edit
              </Button>
              <Button className="flex-1 bg-slate-900 hover:bg-slate-800" onClick={handleCreateProject}>
                Create Project
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AIProjectCreation;
